import React from "react";
import { Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { EntityType } from "../../../../models/enums";
import { ISyncSchedule } from "../../../../models/SyncSchedule";
import { IEntityTypeDependency } from "../../../../models/EntityTypeDependency";
import { ISupportedEntityType } from "../../../../models/CustomerSyncEntityTypes";
import SyncScheduleTree from "./SyncScheduleTree";
import ImpactAnalysisPanel from "./ImpactAnalysisPanel";
import styles from "./SyncSchedulePanel.module.scss";

interface IImpactSummary {
  addedEntities: EntityType[];
  removedEntities: EntityType[];
  missingDependencies: Array<{ missingDep: EntityType; requiredBy: EntityType[] }>;
  hasIssues: boolean;
}

interface IScheduleTabPanelProps {
  schedule: ISyncSchedule;
  index: number;
  scheduleChanges: EntityType[];
  scheduleOriginal: EntityType[];
  supportedEntityTypes: ISupportedEntityType[];
  dependencies: IEntityTypeDependency[];
  pendingChanges: EntityType[];
  loaded: boolean;
  requiresEgress: boolean;
  hasChanges: boolean;
  impactSummary: IImpactSummary;
  getScheduleLabel: (schedule: ISyncSchedule, index: number) => string;
  getDisplayName: (entityType: EntityType) => string;
  onEntityToggle: (entityType: EntityType, enabling: boolean) => void;
}

const ScheduleTabPanel: React.FC<IScheduleTabPanelProps> = ({
  schedule,
  index,
  scheduleChanges,
  scheduleOriginal,
  supportedEntityTypes,
  dependencies,
  pendingChanges,
  loaded,
  requiresEgress,
  hasChanges,
  impactSummary,
  getScheduleLabel,
  getDisplayName,
  onEntityToggle
}) => {
  const hasScheduleChanges = JSON.stringify(scheduleChanges.sort()) !== JSON.stringify(scheduleOriginal.sort());

  return (
    <div>
      <div className={styles.tabPanelInfo}>
        <Icon icon={IconNames.DATABASE} size={12} className={styles.tabPanelInfoIcon} />
        <strong>{scheduleOriginal.length}</strong> of <strong>{supportedEntityTypes.length}</strong> entity types syncing
        {hasScheduleChanges && scheduleChanges.length !== scheduleOriginal.length && (
          <span className={styles.tabPanelInfoChange}>
            → {scheduleChanges.length} after save
          </span>
        )}
      </div>
      <div className={styles.columnsContainer}>
        {/* Left Column - Entity Tree */}
        <div className={styles.column}>
          <div className={styles.panelContainer}>
            <div className={styles.panelHeader}>
              <Icon icon={IconNames.TH_LIST} />
              <h4 className={styles.panelHeaderTitle}>
                {getScheduleLabel(schedule, index)} Entity List{schedule.isChangeQueue ? ' - Change Queue' : ''}
              </h4>
            </div>
            <div className={styles.panelContent}>
              {loaded ? (
                <SyncScheduleTree
                  key={`schedule-tree-${index}`}
                  dependencies={dependencies}
                  configuredEntityTypes={pendingChanges}
                  originalConfiguredEntityTypes={scheduleOriginal}
                  supportedEntityTypes={supportedEntityTypes}
                  onEntityToggle={onEntityToggle}
                />
              ) : (
                <div className={styles.emptyState}>
                  Click "Load Schedule" to view entity types
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Impact Summary */}
        <div className={styles.column}>
          <div className={styles.panelContainer}>
            <div className={styles.panelHeader}>
              <Icon icon={IconNames.PULSE} />
              <h4 className={styles.panelHeaderTitle}>
                Impact Analysis
              </h4>
            </div>
            
            <div className={styles.panelContent}>
              <ImpactAnalysisPanel
                requiresEgress={requiresEgress}
                hasChanges={hasChanges}
                impactSummary={impactSummary}
                getDisplayName={getDisplayName}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleTabPanel;
